// Path: src/components/admin/aReferral.tsx
// Admin component to manage referrals, display statistics, and list conversions
import React, { useEffect, useState, useMemo } from "react";
import { withFirebase, FirebaseContextType } from "../dev/Firebase/Firebase";
import { Card, Statistic, Row, Col, Select, Input, Table, Tag } from "antd";
import styled from "styled-components";
import { collection, onSnapshot, query, getDoc, doc } from "firebase/firestore";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { Search } = Input;

const ReferralContainer = styled.div`
  padding: 24px;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.2);
  border-radius: 8px;
  animation: fadeIn 1.5s ease-in;

  @media (max-width: 768px) {
    padding: 10px;
    .ant-card {
      font-size: 14px;
    }
  }
`;

const ClickableCard = styled(Card)`
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 8px 20px rgba(255, 204, 0, 0.4);
  }

  .ant-statistic-title {
    color: #0427f4;
  }

  .ant-statistic-content {
    color: #ffaa00;
    font-size: 24px;
  }
`;

const FilterContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FilterLabel = styled.span`
  margin-right: 8px;
  font-weight: bold;
  color: #0427f4;
`;

const SearchWrapper = styled.div`
  max-width: 300px;
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th {
    background: #3552fc;
    color: #fff;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

interface Referral {
  id: string;
  referrerUid: string;
  referredUid: string;
  referralCode: string;
  commission: number;
  createdAt: Date;
  referrerName?: string;
  referrerEmail?: string;
  referredName?: string;
  referredEmail?: string;
  status: "Pending" | "Completed";
}

interface User {
  key: string;
  name?: string;
  email?: string;
  userRole?: string;
  tuitionFees?: {
    initiPaymentStatus?: "Pending" | "Completed";
  };
}

interface AdminReferralProps {
  firebase: FirebaseContextType;
}

const AdminReferral: React.FC<AdminReferralProps> = ({ firebase }) => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredReferrals, setFilteredReferrals] = useState<Referral[]>([]);
  const [statusFilter, setStatusFilter] = useState<"all" | "pending" | "completed">("all");
  const [userTypeFilter, setUserTypeFilter] = useState<"all" | "uOLSS" | "uOLSME" | "uOLSLM">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!firebase.firestore) {
      console.error("AdminReferral: Firestore instance not initialized");
      setError("Firestore instance not initialized");
      setLoading(false);
      return;
    }

    // Fetch users for name/email lookup
    const usersQuery = query(collection(firebase.firestore, "users"));
    const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
      const usersList: User[] = snapshot.docs.map((doc) => ({
        key: doc.id,
        name: doc.data().name,
        email: doc.data().email,
        userRole: doc.data().userRole,
        tuitionFees: doc.data().tuitionFees,
      }));
      setUsers(usersList);
      console.log("Fetched users for referral lookup:", usersList);
    }, (err) => {
      console.error("Error fetching users:", err);
      setError("Failed to fetch users: " + err.message);
    });

    // Fetch referrals
    const referralsQuery = query(collection(firebase.firestore, "referral_conversions"));
    const unsubscribeReferrals = onSnapshot(referralsQuery, async (snapshot) => {
      const referralsList: Referral[] = [];
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const referrerDoc = await getDoc(doc(firebase.firestore, "users", data.referrerUid));
        const referredDoc = await getDoc(doc(firebase.firestore, "users", data.referredUid));
        const referredUser = users.find((u) => u.key === data.referredUid);
        referralsList.push({
          id: doc.id,
          referrerUid: data.referrerUid,
          referredUid: data.referredUid,
          referralCode: data.referralCode,
          commission: data.commission,
          createdAt: data.createdAt.toDate(),
          referrerName: referrerDoc.exists() ? referrerDoc.data().name : "Unknown",
          referrerEmail: referrerDoc.exists() ? referrerDoc.data().email : "Unknown",
          referredName: referredDoc.exists() ? referredDoc.data().name : "Unknown",
          referredEmail: referredDoc.exists() ? referredDoc.data().email : "Unknown",
          status: referredUser?.tuitionFees?.initiPaymentStatus || "Pending",
        });
      }
      setReferrals(referralsList);
      setLoading(false);
      console.log("Fetched referrals:", referralsList);
    }, (err) => {
      console.error("Error fetching referrals:", err);
      setError("Failed to fetch referrals: " + err.message);
      setLoading(false);
    });

    return () => {
      unsubscribeUsers();
      unsubscribeReferrals();
    };
  }, [firebase, users]);

  useEffect(() => {
    let filtered: Referral[] = referrals;

    // Apply status filter
    if (statusFilter === "pending") {
      filtered = filtered.filter((referral) => referral.status === "Pending");
    } else if (statusFilter === "completed") {
      filtered = filtered.filter((referral) => referral.status === "Completed");
    }

    // Apply user type filter (case-insensitive)
    if (userTypeFilter !== "all") {
      const normalizedFilter = userTypeFilter.toLowerCase();
      filtered = filtered.filter((referral) => {
        const referrer = users.find((u) => u.key === referral.referrerUid);
        const userRole = referrer?.userRole?.toLowerCase();
        if (normalizedFilter === "uolss") return userRole === "uolss" || userRole === "uolss_col";
        if (normalizedFilter === "uolsme") return userRole === "uolsme" || userRole === "olsme_col";
        if (normalizedFilter === "uolslm") return userRole === "uolslm" || userRole === "olslm_col";
        return false;
      });
    }

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((referral) =>
        `${referral.referrerName || ""} ${referral.referrerEmail || ""} ${referral.referredName || ""} ${referral.referredEmail || ""}`
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      );
    }

    setFilteredReferrals(filtered);
    console.log("Filtered referrals after applying filters:", {
      statusFilter,
      userTypeFilter,
      searchQuery,
      filtered,
    });
  }, [referrals, users, statusFilter, userTypeFilter, searchQuery]);

  const totalReferrals = filteredReferrals.length;
  const totalCommission = filteredReferrals.reduce((sum, referral) => sum + referral.commission, 0);
  const pendingCommissions = filteredReferrals.filter((referral) => referral.status === "Pending").length;

  const columns = [
    {
      title: "Referral Code",
      dataIndex: "referralCode",
      sorter: (a: Referral, b: Referral) => a.referralCode.localeCompare(b.referralCode),
    },
    {
      title: "Referrer",
      dataIndex: "referrerName",
      sorter: (a: Referral, b: Referral) => (a.referrerName || "").localeCompare(b.referrerName || ""),
      render: (name: string, record: Referral) => `${name || "Unknown"} (${record.referrerEmail || "Unknown"})`,
    },
    {
      title: "Referred User",
      dataIndex: "referredName",
      sorter: (a: Referral, b: Referral) => (a.referredName || "").localeCompare(b.referredName || ""),
      render: (name: string, record: Referral) => `${name || "Unknown"} (${record.referredEmail || "Unknown"})`,
    },
    {
      title: "Commission",
      dataIndex: "commission",
      sorter: (a: Referral, b: Referral) => a.commission - b.commission,
      render: (commission: number) => `${(commission * 100).toFixed(0)}%`,
    },
    {
      title: "Status",
      dataIndex: "status",
      sorter: (a: Referral, b: Referral) => a.status.localeCompare(b.status),
      render: (status: string) => <Tag color={status === "Completed" ? "green" : "orange"}>{status}</Tag>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      sorter: (a: Referral, b: Referral) => a.createdAt.getTime() - b.createdAt.getTime(),
      render: (date: Date) => date.toLocaleDateString(),
    },
    {
      title: "Action",
      dataIndex: "referredUid",
      render: (referredUid: string) => (
        <Button
          type="primary"
          onClick={() => navigate(`/admin/evaluation/${referredUid}`)}
          aria-label={`Evaluate referred user ${referredUid}`}
        >
          Evaluate
        </Button>
      ),
    },
  ];

  const handleCardClick = (filter: "all" | "pending" | "completed") => {
    setStatusFilter(filter);
  };

  if (loading) {
    return <div>Loading referrals...</div>;
  }

  if (error) {
    console.log("AdminReferral rendering - error state:", error);
    return <div style={{ color: "red", padding: 24 }}>{error}</div>;
  }

  return (
    <ReferralContainer>
      <Helmet>
        <title>OLS Admin Referrals - Sun Light Meditation</title>
        <meta
          name="description"
          content="Manage OLS referral program, track commissions, and grow our Sun Light Meditation community. Join OLS at olsme.com/OLSStudentProspect."
        />
        <meta
          name="keywords"
          content="OLS admin referrals, Sun Light Meditation, referral management, join OLS"
        />
        <link rel="canonical" href="https://www.olsme.com/admin/referrals" />
      </Helmet>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("all")}>
            <Statistic title="Total Referrals" value={totalReferrals} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("pending")}>
            <Statistic title="Pending Commissions" value={pendingCommissions} />
          </ClickableCard>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <ClickableCard onClick={() => handleCardClick("completed")}>
            <Statistic title="Total Commission" value={(totalCommission * 100).toFixed(0)} suffix="%" />
          </ClickableCard>
        </Col>
      </Row>
      <FilterContainer>
        <div>
          <FilterLabel>Filter by status:</FilterLabel>
          <Select
            value={statusFilter}
            onChange={(value: "all" | "pending" | "completed") => setStatusFilter(value)}
            style={{ width: 200 }}
            aria-label="Filter referrals by status"
          >
            <Option value="all">All statuses</Option>
            <Option value="pending">Pending</Option>
            <Option value="completed">Completed</Option>
          </Select>
        </div>
        <div>
          <FilterLabel>Filter by user type:</FilterLabel>
          <Select
            value={userTypeFilter}
            onChange={(value: "all" | "uOLSS" | "uOLSME" | "uOLSLM") => setUserTypeFilter(value)}
            style={{ width: 200 }}
            aria-label="Filter referrals by user type"
          >
            <Option value="all">All types</Option>
            <Option value="uOLSS">uOLSS</Option>
            <Option value="uOLSME">uOLSME</Option>
            <Option value="uOLSLM">uOLSLM</Option>
          </Select>
        </div>
        <SearchWrapper>
          <FilterLabel>Search referrals:</FilterLabel>
          <Search
            placeholder="Search by name, email, or code"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
            aria-label="Search referrals by name, email, or code"
          />
        </SearchWrapper>
      </FilterContainer>
      <StyledTable
        columns={columns}
        dataSource={filteredReferrals}
        pagination={{ pageSize: 10 }}
        rowKey="id"
        onChange={(pagination, filters, sorter) => console.log("Table params:", pagination, filters, sorter)}
      />
    </ReferralContainer>
  );
};

export default withFirebase(AdminReferral);